const API_BASE = import.meta.env.VITE_API_URL || 'https://api.techtalentaccelerator.com';

async function req(method, endpoint, data = null, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const options = { method, headers };
  if (data) options.body = JSON.stringify(data);

  const res = await fetch(`${API_BASE}${endpoint}`, options);
  const json = await res.json().catch(() => ({}));

  if (!res.ok) throw new Error(json.error || `Erreur ${res.status}`);
  return json;
}

// Auth
export const login = (email, password) =>
  req('POST', '/auth/login', { email, password });

export const getMe = (token) =>
  req('GET', '/auth/me', null, token);

export const changePassword = (data, token) =>
  req('POST', '/auth/change-password', data, token);

// Stats
export const getStats = (token) =>
  req('GET', '/stats', null, token);

// Candidatures
export const getCandidatures = (token, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return req('GET', `/candidatures${qs ? '?' + qs : ''}`, null, token);
};

export const getCandidature = (id, token) =>
  req('GET', `/candidatures/${id}`, null, token);

export const exportContactsCSV = async (token, type = null) => {
  const qs = type && type !== 'tous' ? `?type=${type}` : '';
  const res = await fetch(`${API_BASE}/contacts/export${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `messages-contact-tta-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportCandidaturesCSV = async (token, statut = null) => {
  const qs = statut && statut !== 'toutes' ? `?statut=${statut}` : '';
  const res = await fetch(`${API_BASE}/candidatures/export${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Erreur ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `candidatures-tta-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const updateCandidature = (id, data, token) =>
  req('PUT', `/candidatures/${id}`, data, token);

export const deleteCandidature = (id, token) =>
  req('DELETE', `/candidatures/${id}`, null, token);

// Formations
export const getFormations = (token) =>
  req('GET', '/formations', null, token);

export const createFormation = (data, token) =>
  req('POST', '/formations', data, token);

export const updateFormation = (id, data, token) =>
  req('PUT', `/formations/${id}`, data, token);

export const deleteFormation = (id, token) =>
  req('DELETE', `/formations/${id}`, null, token);

// Articles
export const getArticles = (token, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return req('GET', `/articles${qs ? '?' + qs : ''}`, null, token);
};

export const createArticle = (data, token) =>
  req('POST', '/articles', data, token);

export const updateArticle = (id, data, token) =>
  req('PUT', `/articles/${id}`, data, token);

export const deleteArticle = (id, token) =>
  req('DELETE', `/articles/${id}`, null, token);

// Projets
export const getProjets = (token) =>
  req('GET', '/projets', null, token);

export const createProjet = (data, token) =>
  req('POST', '/projets', data, token);

export const updateProjet = (id, data, token) =>
  req('PUT', `/projets/${id}`, data, token);

// Partenaires
export const getPartenaires = (token) =>
  req('GET', '/partenaires', null, token);

export const createPartenaire = (data, token) =>
  req('POST', '/partenaires', data, token);

export const updatePartenaire = (id, data, token) =>
  req('PUT', `/partenaires/${id}`, data, token);

export const deletePartenaire = (id, token) =>
  req('DELETE', `/partenaires/${id}`, null, token);

// Settings
export const getSettings = (token) =>
  req('GET', '/settings', null, token);

export const saveSettings = (data, token) =>
  req('POST', '/settings', data, token);

// Médiathèque
export const getUploads = (token) =>
  req('GET', '/media', null, token); // route /media évite conflit htaccess avec le dossier uploads/

// Utilisateurs
export const getUsers = (token) =>
  req('GET', '/users', null, token);

export const createUser = (data, token) =>
  req('POST', '/users', data, token);

export const updateUser = (id, data, token) =>
  req('PUT', `/users/${id}`, data, token);

// Journal d'activité
export const getAuditLog = (token, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return req('GET', `/audit-log${qs ? '?' + qs : ''}`, null, token);
};

// Formulaire contact (public)
export const submitContact = (data) =>
  req('POST', '/contact', data);

// Messages contact (admin)
export const getContacts = (token, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return req('GET', `/contacts${qs ? '?' + qs : ''}`, null, token);
};

export const deleteContact = (id, token) =>
  req('DELETE', `/contacts/${id}`, null, token);

// Formulaire candidature (public)
export const submitCandidature = (data) =>
  req('POST', '/candidatures/submit', data);

// Formation publique (front-office, sans token)
export const getPublicFormation = (id) =>
  req('GET', `/public/formations/${id}`);

// CMS — Public (sans token)
export const getPublicPages = () =>
  req('GET', '/public/pages');

export const getPublicPage = (slug) =>
  req('GET', `/public/pages/${slug}?_=${Date.now()}`);

export const getPublicNavigation = () =>
  req('GET', '/public/navigation');

export const getPublicFooter = () =>
  req('GET', '/public/footer');

// CMS — Pages (admin)
export const getPages = (token) =>
  req('GET', '/pages', null, token);

export const getPage = (id, token) =>
  req('GET', `/pages/${id}`, null, token);

export const updatePage = (id, data, token) =>
  req('PUT', `/pages/${id}`, data, token);

export const getPageSections = (pageId, token) =>
  req('GET', `/pages/${pageId}/sections`, null, token);

export const addPageSection = (pageId, data, token) =>
  req('POST', `/pages/${pageId}/sections`, data, token);

export const reorderPageSections = (pageId, order, token) =>
  req('POST', `/pages/${pageId}/sections/reorder`, { order }, token);

export const updateSection = (id, data, token) =>
  req('PUT', `/sections/${id}`, data, token);

export const deleteSection = (id, token) =>
  req('DELETE', `/sections/${id}`, null, token);

// CMS — Navigation (admin)
export const getNavigation = (token) =>
  req('GET', '/navigation', null, token);

export const createNavItem = (data, token) =>
  req('POST', '/navigation', data, token);

export const updateNavItem = (id, data, token) =>
  req('PUT', `/navigation/${id}`, data, token);

export const deleteNavItem = (id, token) =>
  req('DELETE', `/navigation/${id}`, null, token);

export const reorderNavigation = (order, token) =>
  req('POST', '/navigation/reorder', { order }, token);

// CMS — Footer (admin)
export const getFooter = (token) =>
  req('GET', '/footer', null, token);

export const createFooterSection = (data, token) =>
  req('POST', '/footer', data, token);

export const updateFooterSection = (id, data, token) =>
  req('PUT', `/footer/${id}`, data, token);

export const deleteFooterSection = (id, token) =>
  req('DELETE', `/footer/${id}`, null, token);

export const reorderFooter = (order, token) =>
  req('POST', '/footer/reorder', { order }, token);

// Upload fichier
export const uploadFile = async (file, token) => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${API_BASE}/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Erreur upload');
  return json;
};
